import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit3, Trash2, Loader2, GripVertical, ArrowUp, ArrowDown, RotateCcw } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { CUSTOM_FIELD_TYPES, labelToKey, typeLabel } from './custom-fields/fieldTypes';
import { invalidateCustomFields } from '../hooks/useCustomFields';
import { errorText } from '../lib/apiErrors';

const API = process.env.REACT_APP_BACKEND_URL || '';
const ENTITIES = ['contact', 'account', 'deal'];

export default function CustomFieldsTab() {
    const { getAuthHeader } = useAuth();
    const [entity, setEntity] = useState('contact');
    const [fields, setFields] = useState([]);
    const [loading, setLoading] = useState(true);
    // Archived (deactivated) fields still reserve their name, so an admin needs to see and
    // restore them. Off by default: the list is then exactly what it always was.
    const [showArchived, setShowArchived] = useState(false);
    const [restoringId, setRestoringId] = useState(null);
    const [restoreError, setRestoreError] = useState(null);

    // Dialog state
    const [showDialog, setShowDialog] = useState(false);
    const [editingField, setEditingField] = useState(null);
    const [form, setForm] = useState({ label: '', field_type: 'text', required: false, options: [], default_value: '' });
    const [optionInput, setOptionInput] = useState('');
    const [saving, setSaving] = useState(false);

    const loadFields = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/api/admin/custom-fields`, {
                headers: getAuthHeader(),
                params: showArchived ? { entity, include_archived: true } : { entity },
            });
            setFields(res.data || []);
        } catch { toast.error('Failed to load custom fields'); }
        finally { setLoading(false); }
    }, [entity, showArchived, getAuthHeader]);

    useEffect(() => { loadFields(); }, [loadFields]);

    const openAdd = () => {
        setEditingField(null);
        setForm({ label: '', field_type: 'text', required: false, options: [], default_value: '' });
        setShowDialog(true);
    };

    const openEdit = (field) => {
        setEditingField(field);
        setForm({
            label: field.label,
            field_type: field.field_type,
            required: field.required,
            options: field.options || [],
            default_value: field.default_value || '',
        });
        setShowDialog(true);
    };

    const handleSave = async () => {
        if (!form.label.trim()) { toast.error('Label is required'); return; }
        setSaving(true);
        try {
            if (editingField) {
                await axios.put(`${API}/api/admin/custom-fields/${editingField.field_id}`, form, { headers: getAuthHeader() });
                toast.success('Field updated');
            } else {
                await axios.post(`${API}/api/admin/custom-fields`, { ...form, entity }, { headers: getAuthHeader() });
                toast.success('Field created');
            }
            setShowDialog(false);
            invalidateCustomFields(entity);
            loadFields();
        } catch (err) {
            toast.error(errorText(err, 'Failed to save'));
        }
        finally { setSaving(false); }
    };

    const handleDelete = async (fieldId) => {
        if (!window.confirm('Deactivate this field? Existing data will be preserved.')) return;
        try {
            await axios.delete(`${API}/api/admin/custom-fields/${fieldId}`, { headers: getAuthHeader() });
            toast.success('Field deactivated');
            invalidateCustomFields(entity);
            loadFields();
        } catch { toast.error('Failed to delete'); }
    };

    const handleRestore = async (field) => {
        setRestoringId(field.field_id);
        setRestoreError(null);
        try {
            await axios.put(`${API}/api/admin/custom-fields/${field.field_id}`, { active: true }, { headers: getAuthHeader() });
            toast.success(`“${field.label}” restored`);
            invalidateCustomFields(field.entity || entity);
            loadFields();
        } catch (err) {
            // A name clash with a field created since arrives as a plain-string 400.
            const message = errorText(err, 'Failed to restore the field');
            setRestoreError(message);
            toast.error(message);
        } finally { setRestoringId(null); }
    };

    const addOption = () => {
        if (optionInput.trim() && !form.options.includes(optionInput.trim())) {
            setForm(f => ({ ...f, options: [...f.options, optionInput.trim()] }));
            setOptionInput('');
        }
    };

    const removeOption = (opt) => {
        setForm(f => ({ ...f, options: f.options.filter(o => o !== opt) }));
    };

    const showOptions = form.field_type === 'select' || form.field_type === 'multi_select';
    // Archived fields are listed (muted, restorable) but are not live: no merge variables, no edit.
    const isArchived = (f) => f.active === false;
    const liveFields = fields.filter((f) => !isArchived(f));

    return (
        <div className="space-y-4">
            {/* Entity selector */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-1 bg-stone-100 rounded-lg p-1">
                    {ENTITIES.map(e => (
                        <button
                            key={e}
                            onClick={() => setEntity(e)}
                            className={`px-4 py-1.5 rounded-md text-sm font-medium capitalize transition-colors ${
                                entity === e ? 'bg-stone-900 text-white' : 'text-stone-600 hover:text-stone-900'
                            }`}
                        >
                            {e}
                        </button>
                    ))}
                </div>
                <div className="flex items-center gap-4">
                    <label className="inline-flex items-center gap-2 text-sm text-stone-600 cursor-pointer select-none">
                        <input
                            type="checkbox" data-testid="custom-fields-show-archived"
                            checked={showArchived}
                            onChange={e => { setShowArchived(e.target.checked); setRestoreError(null); }}
                            className="rounded border-stone-300"
                        />
                        Show archived
                    </label>
                    <Button size="sm" onClick={openAdd}>
                        <Plus className="w-4 h-4 mr-1" /> Add Field
                    </Button>
                </div>
            </div>

            {restoreError && (
                <div role="alert" data-testid="custom-fields-restore-error"
                     className="px-3 py-2 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700">
                    {restoreError}
                </div>
            )}

            {/* Fields table */}
            {loading ? (
                <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 text-stone-400 animate-spin" /></div>
            ) : fields.length === 0 ? (
                <div className="text-center py-10 text-stone-400">
                    <p className="text-sm">No custom fields for {entity}s yet.</p>
                    <p className="text-xs mt-1">Click "Add Field" to create one.</p>
                </div>
            ) : (
                <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-stone-50 border-b border-stone-200">
                            <tr>
                                <th className="text-left px-4 py-2.5 text-xs font-semibold text-stone-500 uppercase w-8">#</th>
                                <th className="text-left px-4 py-2.5 text-xs font-semibold text-stone-500 uppercase">Label</th>
                                <th className="text-left px-4 py-2.5 text-xs font-semibold text-stone-500 uppercase">Key</th>
                                <th className="text-left px-4 py-2.5 text-xs font-semibold text-stone-500 uppercase">Type</th>
                                <th className="text-center px-4 py-2.5 text-xs font-semibold text-stone-500 uppercase">Required</th>
                                <th className="text-right px-4 py-2.5 text-xs font-semibold text-stone-500 uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {fields.map((f, i) => (
                                <tr key={f.field_id} data-testid="custom-field-row" data-archived={isArchived(f) ? 'true' : undefined}
                                    className={`border-b border-stone-100 hover:bg-stone-50 ${isArchived(f) ? 'bg-stone-50/60 text-stone-400' : ''}`}>
                                    <td className="px-4 py-2.5 text-stone-400">{i + 1}</td>
                                    <td className={`px-4 py-2.5 font-medium ${isArchived(f) ? 'text-stone-500' : 'text-stone-900'}`}>
                                        {f.label}
                                        {isArchived(f) && (
                                            <span data-testid="custom-field-archived-tag"
                                                  className="ml-2 align-middle text-[11px] font-semibold px-1.5 py-0.5 rounded bg-stone-200 text-stone-600">Archived</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-2.5 text-stone-400 font-mono text-xs">{f.key}</td>
                                    <td className="px-4 py-2.5">
                                        <span className="text-xs px-2 py-0.5 rounded-full bg-stone-100 text-stone-600 capitalize">
                                            {typeLabel(f.field_type)}
                                        </span>
                                    </td>
                                    <td className="px-4 py-2.5 text-center">
                                        {f.required ? <span className="text-emerald-600 text-xs font-medium">Yes</span> : <span className="text-stone-300">—</span>}
                                    </td>
                                    <td className="px-4 py-2.5 text-right">
                                        <div className="flex items-center justify-end gap-1">
                                            {isArchived(f) ? (
                                                <button type="button" data-testid="custom-field-restore"
                                                        onClick={() => handleRestore(f)} disabled={restoringId === f.field_id}
                                                        aria-label={`Restore ${f.label}`}
                                                        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-stone-700 border border-stone-300 rounded-md hover:bg-stone-100 disabled:opacity-50">
                                                    {restoringId === f.field_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                                                    Restore
                                                </button>
                                            ) : (
                                                <>
                                                    <button onClick={() => openEdit(f)} className="p-1.5 text-stone-400 hover:text-stone-700"><Edit3 className="w-4 h-4" /></button>
                                                    <button onClick={() => handleDelete(f.field_id)} className="p-1.5 text-stone-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Merge variable info */}
            {liveFields.length > 0 && (
                <div className="bg-stone-50 rounded-lg p-3 text-xs text-stone-500">
                    <p className="font-medium text-stone-600 mb-1">Email Template Merge Variables</p>
                    <div className="flex flex-wrap gap-2">
                        {liveFields.map(f => (
                            <code key={f.key} className="px-1.5 py-0.5 rounded bg-white border border-stone-200 text-stone-700 font-mono">
                                {`{{${f.label.replace(/\s/g, '')}}}`}
                            </code>
                        ))}
                    </div>
                </div>
            )}

            {/* Add/Edit Dialog */}
            <Dialog open={showDialog} onOpenChange={setShowDialog}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>{editingField ? 'Edit' : 'Add'} Custom Field</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div>
                            <label className="block text-xs font-medium text-stone-600 mb-1">Label</label>
                            <input
                                value={form.label}
                                onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
                                placeholder="e.g. Annual Contract Value"
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                            />
                            {form.label && (
                                <p className="text-xs text-stone-400 mt-1">Key: <code className="font-mono">{labelToKey(form.label)}</code></p>
                            )}
                        </div>

                        <div>
                            <label className="block text-xs font-medium text-stone-600 mb-1">Field Type</label>
                            <select
                                value={form.field_type}
                                onChange={e => setForm(f => ({ ...f, field_type: e.target.value }))}
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                            >
                                {CUSTOM_FIELD_TYPES.map(t => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                            </select>
                        </div>

                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <input
                                type="checkbox"
                                checked={form.required}
                                onChange={e => setForm(f => ({ ...f, required: e.target.checked }))}
                                className="rounded border-stone-300"
                            />
                            Required field
                        </label>

                        {showOptions && (
                            <div>
                                <label className="block text-xs font-medium text-stone-600 mb-1">Options</label>
                                <div className="flex gap-2 mb-2">
                                    <input
                                        value={optionInput}
                                        onChange={e => setOptionInput(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addOption())}
                                        placeholder="Type option and press Enter"
                                        className="flex-1 px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                                    />
                                    <Button variant="outline" size="sm" onClick={addOption}>Add</Button>
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                    {form.options.map(opt => (
                                        <span key={opt} className="inline-flex items-center gap-1 px-2 py-1 bg-stone-100 rounded-lg text-xs text-stone-700">
                                            {opt}
                                            <button onClick={() => removeOption(opt)} className="text-stone-400 hover:text-red-500">&times;</button>
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div>
                            <label className="block text-xs font-medium text-stone-600 mb-1">Default Value (optional)</label>
                            <input
                                value={form.default_value || ''}
                                onChange={e => setForm(f => ({ ...f, default_value: e.target.value }))}
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
                        <Button onClick={handleSave} disabled={saving}>
                            {saving ? 'Saving...' : editingField ? 'Update' : 'Create'} Field
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
